import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Helmet} from 'react-helmet';
import {
  markPerformanceStart,
  markPerformanceEnd,
} from '@stencil/hotels-utils/src/performance-utils';
import {tranformSrpBreadcrumbData} from '@stencil/hotels-utils/src/seo-utils';
import * as S from './SRP.styles';
import Breadcrumb from '@stencil/dbreadcrumb';
import {fetchSrpDataThunk} from './redux/thunks';
import {resetFilterAction} from './redux/actions';
import {
  hasDataSelectorFn,
  isSrpLoadingSelectorFn,
  seoBreadCrumbDataFn,
  isSrpSeoSelectorFn,
  seoSrpPageTitleSelectorFn,
  seoHelmetDataSelectorFn,
  faqDataSelectorFn,
  seoViewMoreSelectorFn,
  seoFilterLinksFn,
} from './redux/selectors';

import {pushga} from 'ga-utils';
import NavigationHeader from './containers/NavigationHeader/NavigationHeader';
import Filters from './containers/Filters/Filters';
import HotelList from './containers/HotelList';
import AboutCity from './containers/AboutCity';
import SeoFaq from '../HomePage/containers/SeoFaq';
import useScrollToTopOnFilterApiResponse from '../../hooks/useScrollToTopOnFilterApiResponse';
import SearchWidget from './containers/SearchWidget';
import {gaEcomThunk, gaPageViewThunk} from '@stencil/hotels-ga/src/thunks';
import {gaConfig, appConfig} from '@stencil/hotels-config';
import GoSafeCardBlock from '../SmartEngage/containers/GoSafeCardBlock';
import SmartEngageApiContainer from '../SmartEngage/containers/SmartEngageApiContainer';
import {resetSegmentSliderDataAction} from '@stencil/hotels-common-reducer/src/actions';
import SearchResultsLoader from '../../../stories/components/hotels/Loadingpage/SearchResultsLoader/SearchResultsLoader';

import NavigationHeaderLoader from '../../../stories/components/hotels/Loadingpage/NavigationHeaderLoader/NavigationHeaderLoader';
import {gaGoogleRTThunk} from '@stencil/hotels-ga/src/thunks';
import SeoFiltersCard from '../../../stories/components/hotels/SRP/SeoFiltersCard/SeoFiltersCard';
import {BaseColumnWrap} from '../../../stories/components/CommonStyleUtils/dwebCommon.styles';
import SeoSRPTopContent from '../../../stories/components/hotels/common/SeoSRPTopContent/SeoSRPTopContent';
import {topContentSelector} from '../SRP/redux/selectors';

const SRP = () => {
  const dispatch = useDispatch();
  const showViewMore = useSelector(seoViewMoreSelectorFn);

  const hasData = useSelector(hasDataSelectorFn);
  const isLoading = useSelector(isSrpLoadingSelectorFn);
  const seoBreadCrumbData = useSelector(seoBreadCrumbDataFn);
  const isSeo = useSelector(isSrpSeoSelectorFn);
  const faqData = useSelector(faqDataSelectorFn);
  const seoPageTitle = useSelector(seoSrpPageTitleSelectorFn);
  const hasGAFired = useRef(false);
  const {seoLinks, meta, pageTitle} = useSelector(seoHelmetDataSelectorFn);
  const topContent = useSelector(topContentSelector);
  const {displayOverlay} = useScrollToTopOnFilterApiResponse(
    appConfig.srpNavigationHeaderOpenCloseOffset,
  );

  const filterLinks = useSelector(seoFilterLinksFn);

  const fetchSrpDataThunkCaller = useCallback((arg: boolean) => {
    dispatch(fetchSrpDataThunk(arg));
  }, []);

  const gaPageViewThunkCaller = useCallback(() => {
    dispatch(
      gaPageViewThunk('srp', {}, () => {
        return markPerformanceEnd(
          gaConfig.SrpGAEvents.performanceMarkers.start,
          gaConfig.SrpGAEvents.performanceMarkers.end,
          gaConfig.SrpGAEvents.performanceMarkers.duration,
        );
      }),
    );
    dispatch(gaGoogleRTThunk('results'));
  }, []);

  useEffect(() => {
    if (!hasGAFired.current) markPerformanceStart(gaConfig.SrpGAEvents.performanceMarkers.start);

    if (isSeo)
      pushga({
        event: gaConfig.HotelsDwebGenericEvent,
        eventCategory: gaConfig.SrpGAEvents.category_seo,
        eventAction: gaConfig.SrpGAEvents.action_seo_domLoaded,
      });

    if ((hasData === null && !isLoading) || (isSeo && !hasData)) {
      /**
       * isLoading should be false to avoid dual API calls in the case where
       the call has already been made from the home page when moving from home to SRP

       * In case of SEO, if server call is successful, then hasData will be true and isLoading will be false.
        But, if server call fails, hasData will be false and isLoading will be true, for avoiding unwanted transitions between
        loader and "No more data" component.
       */
      fetchSrpDataThunkCaller(false);
    }

    return () => {
      dispatch(resetSegmentSliderDataAction());
      dispatch(resetFilterAction());
    };
  }, []);

  useEffect(() => {
    if (!hasGAFired.current && hasData) {
      hasGAFired.current = true;
      gaPageViewThunkCaller();
      dispatch(gaEcomThunk(gaConfig.ecommerceEvents.srpImpressionLanding));
    }
  }, [hasData]);

  const linkArray = [
    {
      rel: 'canonical',
      href: 'https://www.goibibo.com/hotels/',
    },
  ];

  const metaWithTimeStamp = [...meta, {name: 'timestamp', content: new Date()}];

  return (
    <>
      <Helmet
        title={(isSeo && !!pageTitle && pageTitle) || 'Results'}
        meta={
          isSeo && meta.length
            ? metaWithTimeStamp
            : [{name: 'robots', content: 'noindex, nofollow'}]
        }
        link={isSeo && seoLinks.length ? seoLinks : linkArray}
      />
      <SearchWidget isSrp />
      <SmartEngageApiContainer page="SRP" />
      <BaseColumnWrap>
        {(hasData !== null && (
          <S.MainSectionWrapper>
            <NavigationHeader />
          </S.MainSectionWrapper>
        )) || <NavigationHeaderLoader />}
        {isSeo && (
          <>
            <S.SeoBreadCrumbOuterWrapperDiv>
              {seoPageTitle && <S.SeoSrpPageTitle>{seoPageTitle}</S.SeoSrpPageTitle>}

              <Breadcrumb data={tranformSrpBreadcrumbData(seoBreadCrumbData)} disableLastLink />
            </S.SeoBreadCrumbOuterWrapperDiv>
          </>
        )}

        <S.BodyWrapperContentStyle>
          <S.LeftSectionWrapperStyle>
            {/* {!isSeo && <OfferWidget />} */}
            <Filters isSeo={isSeo} />
          </S.LeftSectionWrapperStyle>
          <S.RightSectionWrapperStyle>
            {!isSeo && <GoSafeCardBlock ContainerWrapper={S.GoSafeCardBlockWrapper} />}
            {isSeo && (
              <SeoSRPTopContent
                ContainerWrapper={S.GoSafeCardBlockWrapper}
                topContent={topContent}
              />
            )}
            {displayOverlay && <SearchResultsLoader />}
            <HotelList
              fetchMoreFn={fetchSrpDataThunkCaller}
              showViewMore={showViewMore}
              displayOverlay={displayOverlay}
            />
            {!!isSeo && showViewMore && (
              <>
                <AboutCity />
                <SeoFiltersCard filterLinks={filterLinks} />
                {!!faqData?.faq_content && !!faqData?.faq_content.length && (
                  <SeoFaq datakey={'dh_srp_json'} faqData={faqData} />
                )}
              </>
            )}
          </S.RightSectionWrapperStyle>
        </S.BodyWrapperContentStyle>
      </BaseColumnWrap>
    </>
  );
};

export default SRP;
